import React, { Component } from 'react';
import PropTypes from 'prop-types';
import sumBy from 'lodash-es/sumBy';
import styled, { css } from 'styled-components';
import { capitalize, detectMob } from '../../../utilities/helperUtility';
import './index.css';
import ErrorTooltip from '../../../page/components/ErrorTooltip';

const StyledSelect = styled.select`${
  (props) => {
    const { $styled: style } = props;
    return css`
      && {
        padding: 0.5em;
        text-align: center;
        width: 60px;
        float: left;
        font-weight: 400;
        background-color: ${style && style.backgroundColor};
        border-color: ${style && style.borderColor};
      }`;
  }
}`;

export default class TableComponent extends Component {
  constructor(props) {
    super(props);

    this.validateQuantity = this.validateQuantity.bind(this);

    this.state = {
      quantityErrors: false,
      blankValueVariant: null,
    };
  }

  componentDidMount() {
    const { getValidationFn } = this.props;
    if (getValidationFn) getValidationFn(this.validateQuantity);
  }

  componentDidUpdate(prevProps) {
    const { quantityErrors } = this.state;
    const { totalQuantity } = this.props;
    const { totalQuantity: prevTotalQuantity } = prevProps;

    if (quantityErrors && prevTotalQuantity !== totalQuantity) {
      this.setState({ quantityErrors: false });
    }
  }

  validateQuantity() {
    const { translation, totalQuantity, minimumQuantity } = this.props;
    const errors = [];
    let error = false;

    if (totalQuantity <= 0) {
      error = translation.key('missingVariantMessage');
      errors.push(error);
    } else if (totalQuantity < minimumQuantity) {
      const message = translation.key('minimumQuantityMessage') || 'The total quantity must be at least';
      error = `${message}: ${minimumQuantity}`;
      errors.push(error);
    }
    this.setState({ quantityErrors: error });

    return errors;
  }

  onChange(props, list, target) {
    let { value } = target;
    const { name } = target;
    const { columns: { lists }, items, minimumQuantity } = this.props;
    const item = items[list.key];
    const { max = null } = props.attrs[list.key];
    const variantMaxAvailability = this.variantMaxAvailability(item, max);

    if (lists.length == 1 && value < minimumQuantity) {
      value = minimumQuantity;
    }

    props.onChange({ name, value }, variantMaxAvailability);
    this.setState({ blankValueVariant: value === '' ? list.key : null });
  }

  parseTag(list, props) {
    let name = `${list.key}_${props.header}`;
    const { propsStyle = {} } = props;

    const buttonStyle = {
      ...propsStyle.variantsTable,
      ...props.attrs[list.key].style,
    };

    switch (props.type) {
      case 'number':
        const { blankValueVariant, quantityErrors } = this.state;
        const {
          minimumQuantity = 0,
          style,
          translation,
          columns: { lists },
          useLegacyQuantity,
        } = this.props;
        const { title = {}, button = {}, body = {} } = style;
        const { max = null } = props.attrs[list.key];
        const min = lists.length > 1 ? 0 : minimumQuantity;
        const { items } = this.props;
        const item = items[list.key];
        const quantity = item ? item.quantity : 0;
        const variantMaxAvailability = this.variantMaxAvailability(item, max);
        const nextValue = quantity + 1;
        const prevValue = quantity - 1 > min ? quantity - 1 : min;
        const isMobile = detectMob();
        const selected = blankValueVariant === list.key ? '' : quantity;
        const customStyle = {
          backgroundColor: body.backgroundColor,
          borderColor: quantityErrors ? '#CC0000' : '',
        };
        const qtyButtonStyle = {
          ...title,
          backgroundColor: button.backgroundColor,
          color: button.color,
        };
        const inputStyle = {
          ...title,
          backgroundColor: body.backgroundColor,
          color: quantityErrors ? '#CC0000' : title.color,
        };

        if (translation.key('columnQuantity') === props.header) name = `${list.key}_quantity`;

        if (useLegacyQuantity) {
          return (
            <div style={{ position: 'relative' }}>
              {isMobile && (variantMaxAvailability <= 1000)
                ? (
                <StyledSelect
                  $styled={customStyle}
                  name={name}
                  value={selected}
                  onChange={({ target }) => this.onChange(props, list, target)}
                >
                  <option value="" disabled hidden>Choose resource</option>
                  {
                    [...Array(variantMaxAvailability + 1).keys()].map((value) => (
                      <option key={value} value={value}>{value}</option>
                    ))
                  }
                </StyledSelect>
                )
                : (
                <input
                  max={variantMaxAvailability}
                  min={min}
                  name={name}
                  type="number"
                  style={customStyle}
                  onInput={({ target }) => this.onChange(props, list, target)}
                  onBlur={() => this.setState({ blankValueVariant: null })}
                  value={selected}
                />
                )}
            </div>
          );
        }

        return (
          <div className="quantity-counter">
            <button
              className="qty-btn qtyminus"
              style={qtyButtonStyle}
              onClick={() => this.onChange(props, list, { name, value: prevValue })}
              disabled={quantity <= min}
            >
              -
            </button>
            <input
              name={name}
              disabled
              className="qty"
              type="text"
              style={inputStyle}
              value={quantity}
            />
            <button
              className="qty-btn qtyplus"
              style={qtyButtonStyle}
              onClick={() => this.onChange(props, list, { name, value: nextValue })}
              disabled={quantity >= variantMaxAvailability}
            >
              +
            </button>
          </div>
        );
      case 'button':
        return (
          <button {...props.attrs[list.key]} name={name} style={buttonStyle} onClick={props.onClick.bind(this)}>
            {props.content}
          </button>
        );
      default:
        return '';
    }
  }

  variantMaxAvailability(item, max) {
    const { capacityType, items } = this.props;
    if (parseInt(capacityType, 10) === 1 || !max) return max;
    const otherItems = Object.values(items).filter((i) => i !== item);
    const quantity = sumBy(otherItems, 'quantity');
    return max > quantity ? max - quantity : 0;
  }

  errorMessage(list) {
    const { columns, extraColumns, limitedSpacesMessage, style } = this.props;
    const column = extraColumns.find((col) => col.type === 'number');

    if (!column) return null;

    const { max = null } = column.attrs[list.key];
    const { errorText } = style;
    if (max >= this.quantity(list)) return null;

    return (
      <tr>
        <td colSpan={extraColumns.length + columns.headers.length}>
          <p style={{ color: errorText.color, float: 'right', margin: 0 }}>
            {limitedSpacesMessage || 'Sorry, limited spaces available'}
          </p>
        </td>
      </tr>
    );
  }

  quantity(list) {
    const { capacityType, items } = this.props;
    const item = items[list.key];
    let quantity;

    if (capacityType === 1) {
      quantity = item ? item.quantity : 0;
    } else {
      const attrs = this.getAttrs();
      const { max = 0 } = attrs[list.key];
      quantity = item && parseInt(max, 10) !== 0 ? sumBy(Object.values(items), 'quantity') : 0;
    }
    const unitMultiplier = item ? item.unit_multiplier : 1;
    return quantity * unitMultiplier;
  }

  columnHead() {
    const { columns } = this.props;
    return columns.headers.map((header, i) => (
      <th style={{ minWidth: '45px', fontSize: this.fontSize() }} key={header + i}>
        <p>{capitalize(header)}</p>
      </th>
    ));
  }

  columnBody(list) {
    const { columns } = this.props;
    return columns.headers.map((header, i) => <td style={{ fontSize: this.fontSize() }} key={list[header] + list.key + i}>{list[header]}</td>);
  }

  fontSize() {
    const { style } = this.props;
    if (!style) return 'inherit';
    const fontSize = style.body ? style.body.fontSize : '14px';
    return fontSize >= '14px' ? `calc(${fontSize || '16px'} - 2px)` : '12px';
  }

  extraColumnsHead() {
    const { extraColumns } = this.props;
    return extraColumns.map((data) => (
      <th style={{ fontSize: this.fontSize() }} key={data.header}>
        <p>{capitalize(data.header)}</p>
      </th>
    ));
  }

  extraColumnsBody(list) {
    const { extraColumns } = this.props;
    return extraColumns.map((data, i) => (
      <th style={{ fontSize: this.fontSize() }} key={data.header + list.key + i}>{this.parseTag(list, data)}</th>
    ));
  }

  getAttrs() {
    const { extraColumns } = this.props;
    const column = extraColumns.find((col) => col.type === 'number');
    return column ? column.attrs : {};
  }

  render() {
    const {
      capacityType,
      className,
      columns: { lists },
      columnReverse,
      hideHead,
    } = this.props;
    const { quantityErrors } = this.state;
    const attrs = this.getAttrs();

    let listIndex;
    Object.keys(attrs).some((x, i) => {
      listIndex = i;
      return parseInt(attrs[x].max, 10) !== 0;
    });

    return (
      <section className="fixed-thead" style={hideHead ? { paddingTop: '0' } : {}}>
        <div className="table-container">
          {quantityErrors
            && <ErrorTooltip message={quantityErrors} />}
          <table className={`pure-table pure-form center fluid ${className}`}>
            <thead style={hideHead ? { display: 'none' } : {}}>
              <tr>
                {columnReverse && this.extraColumnsHead()}
                {this.columnHead()}
                {!columnReverse && this.extraColumnsHead()}
              </tr>
            </thead>
            <tbody>
              {lists.map((list, i) => (
                <React.Fragment key={list.key + i}>
                <tr key={list.key + i}>
                  {columnReverse && this.extraColumnsBody(list)}
                  {this.columnBody(list)}
                  {!columnReverse && this.extraColumnsBody(list)}
                </tr>
                {capacityType === 1 && this.errorMessage(list)}
                </React.Fragment>
              ))}
              {lists.length && capacityType !== 1 && this.errorMessage(lists[listIndex || 0])}
            </tbody>
          </table>
        </div>
      </section>
    );
  }
}

TableComponent.propTypes = {
  columns: PropTypes.shape({
    headers: PropTypes.array.isRequired,
    lists: PropTypes.array.isRequired,
  }),
  extraColumns: PropTypes.arrayOf(
    PropTypes.shape({
      quantity: PropTypes.string,
      type: PropTypes.string.isRequired,
      onChange: PropTypes.func,
    }),
  ),
  useLegacyQuantity: PropTypes.bool,
};
